// frontend/src/Components/PixBimPolygon.tsx
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Graphics as GraphicsType } from "pixi.js";
import { Container, EventMode, FederatedPointerEvent, Graphics, Text, Texture } from "pixi.js";
import { extend } from "@pixi/react";

import { IPixPolygon } from "../types";
import { getCenterOfPolygon, getPolygonDimension } from "../utils/geo";

extend({ Container, Graphics, Text });

interface PixBimPolygonProps {
  polygon: IPixPolygon;
  onPointerDown?: (
    e: FederatedPointerEvent,
    polygon: IPixPolygon,
    coordinates: [number, number]
  ) => void;
  onClick?: (e: FederatedPointerEvent, polygon: IPixPolygon, coordinates: [number, number]) => void;
  onRightClick?: (e: FederatedPointerEvent, polygon: IPixPolygon) => void;
  onPolygonHover?: (e: FederatedPointerEvent, tooltipText?: string) => void;
  angle?: number;
}

export interface PixBimPolygonType {
  setNotPreSelected: () => void;
  setPreSelected: () => void;
}

const PixBimPolygon = forwardRef<PixBimPolygonType, PixBimPolygonProps>(
  ({ polygon, onClick, onRightClick, angle = 0, onPolygonHover, onPointerDown }, ref) => {
    const [fillColor, setFillColor] = useState(polygon.fillColor || "#fafafa");
    const [strokeColor, setStrokeColor] = useState(polygon.strokeColor || "#000000");
    const graphicsRef = useRef<GraphicsType | null>(null);

    // Memoized strokeWidth – super viktigt för stabilitet!
    const strokeWidth = useMemo(() => {
      if (typeof polygon.strokeWidth === "string") {
        return parseFloat(polygon.strokeWidth.replace("px", "")) || 1;
      }
      return polygon.strokeWidth ?? 1;
    }, [polygon.strokeWidth]);

    const { text, points, isMoveable, rightClickMenu, isNotClickable } = polygon;
    const { values: textValues, color: textColor = "#000", show: textShow } = text || {};

    // Synka fillColor
    useEffect(() => {
      setFillColor(polygon.fillColor || "#fafafa");
    }, [polygon.fillColor]);

    // Synka strokeColor (detta var det stora felet!)
    useEffect(() => {
      setStrokeColor(polygon.strokeColor || "#000000");
    }, [polygon.strokeColor]);

    const polygonValues = useCallback(() => {
      const flat = points.flat();
      const { width, height } = getPolygonDimension(flat);
      return {
        center: getCenterOfPolygon(points),
        width,
        height,
      };
    }, [points]);

    useImperativeHandle(ref, () => ({
      setNotPreSelected: () => drawAgain(),
      setPreSelected: () => drawAgain("#e1eaf5"),
    }));

    const drawAgain = (overrideFill = fillColor) => {
      const g = graphicsRef.current;
      if (!g) return;

      const texture = Texture.WHITE;
      texture.source.scaleMode = "nearest";

      g.clear();
      g.poly(points.flat());
      g.fill({ texture, color: overrideFill });
      g.stroke({ color: strokeColor, width: strokeWidth, texture });
      if (isMoveable) g.cursor = "pointer";
      g.eventMode = "static";
    };

    const draw = useCallback(
      (g: GraphicsType) => {
        const texture = Texture.WHITE;
        texture.source.scaleMode = "nearest";

        g.clear();
        g.poly(points.flat());
        g.fill({ texture, color: fillColor, alpha: polygon.opacity ?? 1 });
        g.stroke({ color: strokeColor, width: strokeWidth, texture });
        if (isMoveable) g.cursor = "pointer";
        g.eventMode = "static";
      },
      [points, fillColor, strokeColor, strokeWidth, isMoveable, polygon.opacity]
    );

    const handleClick = (e: FederatedPointerEvent) => {
      onClick?.(e, polygon, [0, 0]);
    };

    const handlePointerDown = (e: FederatedPointerEvent) => {
      if (onPointerDown) {
        e.stopPropagation();
        onPointerDown(e, polygon, [0, 0]);
      }
    };

    const handleRightClick = (e: FederatedPointerEvent) => {
      if (onRightClick && rightClickMenu) {
        e.preventDefault();
        e.stopPropagation();
        onRightClick(e, polygon);
      }
    };

    const handleHover = (e: FederatedPointerEvent) => {
      if (onPolygonHover && polygon.tooltip) {
        onPolygonHover(e, polygon.tooltip);
      }
    };

    const { center: polygonCenter, width, height } = polygonValues();
    const wide = width < height;
    const longSize = wide ? width : height;
    const sizeConstant = (longSize > 250 ? 250 : longSize) / 6;
    const textAngle = (wide ? 90 : 0) - angle;

    return (
      <container
        eventMode={isNotClickable ? "none" : "static"}
        cursor={isMoveable ? "pointer" : "default"}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onRightClick={handleRightClick}
        onPointerOver={handleHover}
      >
        <graphics ref={graphicsRef} draw={draw} />

        {textShow && textValues && (
          <pixiText
            eventMode="none"
            text={textValues.join("\n")}
            style={{ fontSize: sizeConstant, align: "center", fill: textColor }}
            anchor={{ x: 0.5, y: 0.5 }}
            position={polygonCenter}
            angle={textAngle}
            zIndex={1}
          />
        )}
      </container>
    );
  }
);

PixBimPolygon.displayName = "PixBimPolygon";

export default React.memo(PixBimPolygon);